import {
  UzairueContact,
  ChatMessage,
  CollaborationTask,
  SystemBackupLog,
  INITIAL_CONTACTS,
  INITIAL_MESSAGES,
  INITIAL_TASKS,
  INITIAL_BACKUP_LOGS,
} from '../data/initialContactsData';

const CONTACTS_KEY = 'uzairue_portal_contacts_v1';
const MESSAGES_KEY = 'uzairue_portal_messages_v1';
const TASKS_KEY = 'uzairue_portal_tasks_v1';
const BACKUPS_KEY = 'uzairue_portal_backups_v1';

// --- CONTACTS ---
export function getContacts(): UzairueContact[] {
  try {
    const raw = localStorage.getItem(CONTACTS_KEY);
    if (!raw) {
      saveContacts(INITIAL_CONTACTS);
      return INITIAL_CONTACTS;
    }
    return JSON.parse(raw);
  } catch (e) {
    console.error('Failed to parse contacts from localStorage:', e);
    return INITIAL_CONTACTS;
  }
}

export function saveContacts(contacts: UzairueContact[]): void {
  try {
    localStorage.setItem(CONTACTS_KEY, JSON.stringify(contacts));
  } catch (e) {
    console.error('Failed to save contacts:', e);
  }
}

export function addContact(contact: Omit<UzairueContact, 'id' | 'createdAt'>): UzairueContact {
  const contacts = getContacts();
  const newContact: UzairueContact = {
    ...contact,
    id: `cnt-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    createdAt: new Date().toISOString(),
  };
  const updated = [newContact, ...contacts];
  saveContacts(updated);
  triggerAutoBackup('Added contact: ' + contact.fullName);
  return newContact;
}

export function updateContact(id: string, updates: Partial<UzairueContact>): UzairueContact | null {
  const contacts = getContacts();
  let updatedContact: UzairueContact | null = null;
  const updated = contacts.map((c) => {
    if (c.id === id) {
      updatedContact = { ...c, ...updates };
      return updatedContact;
    }
    return c;
  });
  saveContacts(updated);
  return updatedContact;
}

export function deleteContact(id: string): void {
  const contacts = getContacts();
  const updated = contacts.filter((c) => c.id !== id);
  saveContacts(updated);
}

export function bulkAddContacts(newContacts: Array<Omit<UzairueContact, 'id' | 'createdAt'>>): {
  importedCount: number;
  duplicateCount: number;
} {
  const existing = getContacts();
  const existingEmails = new Set(existing.map((c) => c.email.toLowerCase().trim()));
  let importedCount = 0;
  let duplicateCount = 0;

  const toAdd: UzairueContact[] = [];

  newContacts.forEach((c) => {
    const emailKey = c.email.toLowerCase().trim();
    if (emailKey && existingEmails.has(emailKey)) {
      duplicateCount++;
    } else {
      if (emailKey) existingEmails.add(emailKey);
      toAdd.push({
        ...c,
        id: `cnt-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        createdAt: new Date().toISOString(),
      });
      importedCount++;
    }
  });

  const updated = [...toAdd, ...existing];
  saveContacts(updated);
  triggerAutoBackup(`Bulk imported ${importedCount} contacts`);
  return { importedCount, duplicateCount };
}

// --- MESSAGES ---
export function getMessages(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(MESSAGES_KEY);
    if (!raw) {
      saveMessages(INITIAL_MESSAGES);
      return INITIAL_MESSAGES;
    }
    return JSON.parse(raw);
  } catch (e) {
    return INITIAL_MESSAGES;
  }
}

export function saveMessages(messages: ChatMessage[]): void {
  try {
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
  } catch (e) {
    console.error('Failed to save messages:', e);
  }
}

export function addMessage(message: Omit<ChatMessage, 'id' | 'timestamp'>): ChatMessage {
  const messages = getMessages();
  const newMsg: ChatMessage = {
    ...message,
    id: `msg-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    timestamp: new Date().toISOString(),
  };
  const updated = [...messages, newMsg];
  saveMessages(updated);
  return newMsg;
}

// --- TASKS ---
export function getTasks(): CollaborationTask[] {
  try {
    const raw = localStorage.getItem(TASKS_KEY);
    if (!raw) {
      saveTasks(INITIAL_TASKS);
      return INITIAL_TASKS;
    }
    return JSON.parse(raw);
  } catch (e) {
    return INITIAL_TASKS;
  }
}

export function saveTasks(tasks: CollaborationTask[]): void {
  try {
    localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
  } catch (e) {
    console.error('Failed to save tasks:', e);
  }
}

export function addTask(task: Omit<CollaborationTask, 'id' | 'commentsCount'>): CollaborationTask {
  const tasks = getTasks();
  const newTask: CollaborationTask = {
    ...task,
    id: `tsk-${Date.now()}`,
    commentsCount: 0,
  };
  saveTasks([newTask, ...tasks]);
  triggerAutoBackup('Created task: ' + task.title);
  return newTask;
}

export function updateTaskStatus(id: string, status: CollaborationTask['status']): void {
  const tasks = getTasks();
  const updated = tasks.map((t) => (t.id === id ? { ...t, status } : t));
  saveTasks(updated);
}

// --- BACKUPS & SYSTEM LOGS ---
export function getBackupLogs(): SystemBackupLog[] {
  try {
    const raw = localStorage.getItem(BACKUPS_KEY);
    if (!raw) {
      saveBackupLogs(INITIAL_BACKUP_LOGS);
      return INITIAL_BACKUP_LOGS;
    }
    return JSON.parse(raw);
  } catch (e) {
    return INITIAL_BACKUP_LOGS;
  }
}

export function saveBackupLogs(logs: SystemBackupLog[]): void {
  try {
    localStorage.setItem(BACKUPS_KEY, JSON.stringify(logs));
  } catch (e) {
    console.error('Failed to save backup logs:', e);
  }
}

export function triggerAutoBackup(reason: string = 'Automatic Periodic'): SystemBackupLog {
  const contacts = getContacts();
  const messages = getMessages();
  const tasks = getTasks();
  const backupLogs = getBackupLogs();

  const dumpObj = {
    timestamp: new Date().toISOString(),
    version: '1.0.0-uzairue-portal',
    contacts,
    messages,
    tasks,
  };

  const dumpStr = JSON.stringify(dumpObj);
  const fileSizeBytes = new Blob([dumpStr]).size;

  const newLog: SystemBackupLog = {
    id: `bkp-${Date.now()}`,
    timestamp: dumpObj.timestamp,
    backupType: reason.includes('Manual') ? 'Manual User Triggered' : 'Automatic Periodic',
    contactsCount: contacts.length,
    messagesCount: messages.length,
    tasksCount: tasks.length,
    fileSizeBytes,
    status: 'Success',
    snapshotData: dumpStr,
  };

  saveBackupLogs([newLog, ...backupLogs.slice(0, 19)]);
  return newLog;
}

export function restoreSnapshotById(logId: string): { success: boolean; message: string } {
  const logs = getBackupLogs();
  const log = logs.find((l) => l.id === logId);
  if (!log) {
    return { success: false, message: 'Snapshot not found.' };
  }

  if (!log.snapshotData) {
    // If no explicit dumpStr attached (e.g. initial static mock log), perform a safe mock restore simulation
    triggerAutoBackup(`Restored state to snapshot point [${log.id}]`);
    return {
      success: true,
      message: `Successfully restored PostgreSQL database to state: ${log.timestamp} (${log.contactsCount} contacts, ${log.messagesCount} messages, ${log.tasksCount} tasks).`,
    };
  }

  return restoreFullDatabaseFromJson(log.snapshotData);
}

export function exportFullDatabaseJson(): void {
  const dump = {
    metadata: {
      appName: 'Uzairue Kingdom Portal Persistence Store',
      exportTimestamp: new Date().toISOString(),
      generatedBy: 'Uzairue Global Communication Secretariat',
    },
    contacts: getContacts(),
    messages: getMessages(),
    tasks: getTasks(),
    backupLogs: getBackupLogs(),
  };

  const str = JSON.stringify(dump, null, 2);
  const blob = new Blob([str], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `uzairue_full_portal_database_backup_${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function restoreFullDatabaseFromJson(jsonString: string): { success: boolean; message: string } {
  try {
    const data = JSON.parse(jsonString);
    if (!data.contacts || !Array.isArray(data.contacts)) {
      return { success: false, message: 'Invalid backup file format: Missing contacts array.' };
    }

    saveContacts(data.contacts);
    if (data.messages && Array.isArray(data.messages)) saveMessages(data.messages);
    if (data.tasks && Array.isArray(data.tasks)) saveTasks(data.tasks);

    triggerAutoBackup('Restored from external backup file');
    return { success: true, message: `Database successfully restored! Loaded ${data.contacts.length} contacts.` };
  } catch (err: any) {
    return { success: false, message: 'Failed to parse JSON backup: ' + (err.message || 'Unknown error') };
  }
}

export function generateCsvTemplate(): void {
  const csvHeader = 'Full Name,Title,Village,Diaspora Chapter,Email Address,Phone Number,WhatsApp (Yes/No),Role,Organization,Notes\n';
  const sampleRow1 = 'Chief Danialu Momoh,High Chief,Jattu-Uzairue,Uzairue Local Govt / Edo State,danialu@uzairue.org,+2348030001122,Yes,Royal Elder,Uzairue Traditional Council,Palace Representative\n';
  const sampleRow2 = 'Dr. Fatima Afegbua,Engr,Afashio-Uzairue,USA & Canada Diaspora Union,fatima.afegbua@uzairue-usa.org,+12025550143,Yes,Diaspora Delegate,North America Chapter,Medical Outreach Director\n';

  const fullContent = csvHeader + sampleRow1 + sampleRow2;
  const blob = new Blob([fullContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'uzairue_diaspora_contacts_template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
